// @flow

import _ from 'lodash/fp';
import React, {Component} from 'react';
import {touch, isValid, getFormValues, reset as resetForm} from 'redux-form';
import {connect} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {
  fetchRoomTypes,
  uploadProjectFile,
  removeProjectFile,
  abortFileUpload,
  abortAllUploads,
  setActiveRoomType,
  uploadRoomTypeImage,
  removeRoomTypeImage,
  createProject,
  reset,
  clear,
} from '../../actions/projectView';
import {setDirty} from '../../actions/form';
import FileDropzone from '../../components/FileDropzone/FileDropzone';
import RoomTypeImages from '../../components/RoomTypeImages/RoomTypeImages';
import ProjectForm from '../../components/ProjectForm/ProjectForm';
import ServerError from '../../components/ServerError/ServerError';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import SaveMenu from '../../components/SaveMenu/SaveMenu';
import Navigation from '../../components/Navigation/Navigation';
import validator from '../../helpers/validator';

const createValidation = {
  name: {rule: 'string|required', label: 'Namn'},
  propertyNumber: {rule: 'string|required', label: 'Fastighetsnummer'},
  propertyDescription: {rule: 'string|required', label: 'Fastighetsbeteckning'},
};

export type Props = {
  isFetchingRoomTypes: boolean,
  activeRoomType: ?any,
  roomTypes: any[],
  projectFiles: Object,
  projectFileUploads: Object,
  projectFilesUploadIds: Object,
  roomTypeImages: any[],
  roomTypeImageUploads: Object,
  roomTypeImagesUploadIds: Object,
  fetchRoomTypes: Function,
  uploadProjectFile: Function,
  removeProjectFile: Function,
  uploadRoomTypeImage: Function,
  removeRoomTypeImage: Function,
  abortFileUpload: Function,
  abortAllUploads: Function,
  setActiveRoomType: Function,
  isFetching: boolean,
  prevPath: string,
  project: ?any,
  error: ?any,
  projectFormValues: any,
  projectFormIsValid: boolean,
  createProject: Function,
  touch: Function,
  clear: Function,
  resetForm: Function,
  setDirty: Function,
  reset: Function,
  isDirty: boolean,
};

class ProjectCreationContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    this.props.fetchRoomTypes();
  }

  componentWillUnmount() {
    this.props.abortAllUploads(
      this.props.projectFilesUploadIds,
      this.props.roomTypeImagesUploadIds
    );
    this.props.clear();
  }

  setDirty() {
    const {
      setDirty,
      isDirty,
    } = this.props;

    if (!isDirty) {
      setDirty(true);
    }
  }

  reset() {
    const {
      setDirty,
      reset,
      resetForm,
    } = this.props;
    reset();
    resetForm('project');
    setDirty(false);
  }

  save() {
    const {
      projectFiles,
      roomTypeImages,
      projectFormValues,
      projectFormIsValid,
      createProject,
      touch,
    } = this.props;
    const projectFields = _.keys(createValidation);
    touch('project', ...projectFields);

    if (!projectFormIsValid) {
      return;
    }

    const data = {
      ...projectFormValues,
      ...projectFiles,
      images: roomTypeImages,
    };

    createProject(data);
  }

  render() {
    const {
      activeRoomType,
      isFetchingRoomTypes,
      roomTypes,
      projectFiles,
      projectFileUploads,
      uploadProjectFile,
      abortFileUpload,
      removeProjectFile,
      setActiveRoomType,
      roomTypeImages,
      roomTypeImageUploads,
      uploadRoomTypeImage,
      removeRoomTypeImage,
      project,
      error,
      isFetching,
      prevPath,
      isDirty,
    } = this.props;

    return (
      <div className="relative">
        <div className="row align-justify align-middle content-header">
          <div className="column shrink">
            <div className="row no-row-margins align-middle">
              <Navigation />
              <div className="content-title">
                Nytt projekt
              </div>
            </div>
          </div>
        </div>
        <LoadingIndicator isLoading={isFetching || isFetchingRoomTypes} />
        <ServerError error={error} />
        <div className="content-section box">
          <div className="row">
            <div className="column small-6">
              <ProjectForm
                onUserChange={() => this.setDirty()}
                validate={values => validator(createValidation, values)} />
              {project &&
                <Redirect to={prevPath} />
              }
            </div>
            <div className="column small-6">
              <FileDropzone
                label="Cover image"
                accept="image/png, image/jpg, image/jpeg, image/bmp"
                preview={true}
                file={projectFiles.coverImage}
                upload={projectFileUploads.coverImage}
                onUpload={f => uploadProjectFile('coverImage', f)}
                onUploadAbort={u => abortFileUpload(u.tempId)}
                onRemove={() => removeProjectFile('coverImage')}
                onChange={() => this.setDirty()} />
              <FileDropzone
                label="Broschyr"
                file={projectFiles.brochure}
                upload={projectFileUploads.brochure}
                onUpload={f => uploadProjectFile('brochure', f)}
                onUploadAbort={u => abortFileUpload(u.tempId)}
                onRemove={() => removeProjectFile('brochure')}
                onChange={() => this.setDirty()} />
            </div>
          </div>
        </div>
        <div className="content-section box">
          <div className="row">
            <div className="column small-6">
            </div>
            <div className="column small-6">
              <RoomTypeImages
                activeRoomType={activeRoomType}
                roomTypes={roomTypes}
                images={roomTypeImages}
                uploads={roomTypeImageUploads}
                onRoomTypeSelect={r => setActiveRoomType(r)}
                onUploadAbort={u => abortFileUpload(u.tempId)}
                onUpload={f => {
                  this.setDirty();
                  if (!!activeRoomType) {
                    uploadRoomTypeImage(activeRoomType.id, f);
                  }
                }}
                onRemove={i => {
                  this.setDirty();
                  removeRoomTypeImage(i.id);
                }} />
            </div>
          </div>
        </div>
        {isDirty &&
          <SaveMenu
            isLoading={isFetching}
            onDiscard={() => this.reset()}
            onSave={() => this.save()}/>
        }
      </div>
    );
  }
}

const getProjectFileUploads = (uploadIds, files) =>
  _.mapValues(id => files.byId[id], uploadIds) || {};

const getRoomTypeImagesUploads = (uploadIds, files) =>
  _.mapValues(_.map(u => files.byId[u]), uploadIds) || {};

const mapStateToProps = ({
  projectView: {
    project: {
      files: projectFiles,
      roomTypeImages,
      active: project,
      error,
      isFetching,
    },
    roomTypes: {
      list: roomTypes,
      isFetching: isFetchingRoomTypes,
    },
    roomType: {
      active: activeRoomType,
    },
    uploadIds: {
      projectFiles: projectFilesUploadIds,
      roomTypeImages: roomTypeImagesUploadIds,
    },
    isDirty,
  },
  files,
  form,
}) => ({
  project,
  error,
  isFetching,
  isFetchingRoomTypes,
  roomTypes,
  projectFiles,
  projectFilesUploadIds,
  activeRoomType,
  roomTypeImages,
  roomTypeImagesUploadIds,
  roomTypeImageUploads: getRoomTypeImagesUploads(roomTypeImagesUploadIds, files),
  projectFileUploads: getProjectFileUploads(projectFilesUploadIds, files),
  projectFormIsValid: isValid('project')({form}),
  projectFormValues: getFormValues('project')({form}),
  isDirty,
});

const mapDispatchToProps = dispatch => ({
  fetchRoomTypes: () =>
    dispatch(fetchRoomTypes()),
  removeProjectFile: name =>
    dispatch(removeProjectFile(name)),
  abortFileUpload: tempId =>
    dispatch(abortFileUpload(tempId)),
  abortAllUploads: (uploads, roomTypeImageUploads) =>
    dispatch(abortAllUploads(uploads, roomTypeImageUploads)),
  uploadProjectFile: (name, [file, ...rest]) => {
    if (file) {
      dispatch(uploadProjectFile(name, file));
    }
  },
  uploadRoomTypeImage: (roomTypeId, [file, ...rest]) => {
    if (file) {
      dispatch(uploadRoomTypeImage(roomTypeId, file));
    }
  },
  removeRoomTypeImage: fileId =>
    dispatch(removeRoomTypeImage(fileId)),
  setActiveRoomType: roomType =>
    dispatch(setActiveRoomType(roomType)),
  createProject: data =>
    dispatch(createProject(data)),
  touch: (form, ...fields) =>
    dispatch(touch(form, ...fields)),
  clear: () =>
    dispatch(clear()),
  setDirty: dirty =>
    dispatch(setDirty(dirty, 'project')),
  resetForm: form =>
    dispatch(resetForm(form)),
  reset: () =>
    dispatch(reset()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectCreationContainer);



// WEBPACK FOOTER //
// ./src/containers/ProjectCreationContainer/ProjectCreationContainer.js